import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export interface ActivityCategory {
  _id: string;
  name: string;
  description?: string;
  color?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CategoryInput {
  name: string;
  description?: string;
  color?: string;
  isActive?: boolean;
}

interface ActivityCategoryState {
  categories: ActivityCategory[];
  loading: boolean;
  saving: boolean;

  fetchCategories: () => Promise<void>;
  createCategory: (data: CategoryInput) => Promise<boolean>;
  updateCategory: (id: string, data: Partial<CategoryInput>) => Promise<boolean>;
  deleteCategory: (id: string) => Promise<void>;
}

export const useActivityCategoryStore = create<ActivityCategoryState>(
  (set, get) => ({
    categories: [],
    loading: false,
    saving: false,

    fetchCategories: async () => {
      set({ loading: true });
      try {
        const { data } = await axiosInstance.get("/activity-categories");
        set({ categories: data.categories });
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message || "Failed to load categories"
        );
      } finally {
        set({ loading: false });
      }
    },

    createCategory: async (payload) => {
      set({ saving: true });
      try {
        const { data } = await axiosInstance.post(
          "/activity-categories",
          payload
        );
        set({ categories: [...get().categories, data.category] });
        toast.success("Category created");
        return true;
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message || "Failed to create category"
        );
        return false;
      } finally {
        set({ saving: false });
      }
    },

    updateCategory: async (id, payload) => {
      set({ saving: true });
      try {
        const { data } = await axiosInstance.put(
          `/activity-categories/${id}`,
          payload
        );
        set({
          categories: get().categories.map((c) =>
            c._id === id ? data.category : c
          ),
        });
        toast.success("Category updated");
        return true;
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message || "Failed to update category"
        );
        return false;
      } finally {
        set({ saving: false });
      }
    },

    deleteCategory: async (id) => {
      try {
        await axiosInstance.delete(`/activity-categories/${id}`);
        set({ categories: get().categories.filter((c) => c._id !== id) });
        toast.success("Category deleted");
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message || "Failed to delete category"
        );
      }
    },
  })
);
